class PredictiveSearch extends SearchForm {
  static productIndex = [];
  static collectionIndex = [];
  static catalogPromise = null;

  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.requestId = 0;

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    PredictiveSearch.ensureCatalog();

    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (!currentButtonText || !previousTerm) return;

    try {
      const matches = currentButtonText.match(new RegExp(previousTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'));
      if (!matches || matches.length > 1) return;
      searchForTextElement.innerText = currentButtonText.replace(previousTerm, newTerm);
    } catch (error) {
      // Ignore invalid previous terms
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(/\s+/g, '-').toLowerCase();
    const requestId = ++this.requestId;
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      const searchDeferred = this.dispatchSearchUpdateEvent(searchTerm);
      searchDeferred?.resolve({ totalCount: this.getTotalResultCount() });
      return;
    }

    const searchDeferred = this.dispatchSearchUpdateEvent(searchTerm);

    // Cancel any in-flight request so second/third searches always work
    this.abortController.abort();
    this.abortController = new AbortController();

    const params = new URLSearchParams();
    params.set('q', searchTerm);
    params.set('section_id', 'predictive-search');
    params.set('resources[limit]', '10');
    params.set('resources[limit_scope]', 'each');
    params.set('resources[type]', 'query,product,collection');
    params.set('options[prefix]', 'last');
    params.set('options[unavailable_products]', 'last');

    fetch(`${routes.predictive_search_url}?${params.toString()}`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        if (requestId !== this.requestId || this.getQuery() !== searchTerm) return null;

        const parsedDoc = new DOMParser().parseFromString(text, 'text/html');
        const resultsMarkup = parsedDoc.querySelector('#shopify-section-predictive-search')?.innerHTML;
        const shopifyProductCount = parsedDoc.querySelectorAll(
          '#predictive-search-results-products-list .predictive-search__list-item'
        ).length;

        if (shopifyProductCount > 0) {
          return { markup: resultsMarkup, cacheable: true };
        }

        // Shopify returned no products for short/partial queries — fill from local catalog
        return this.buildLocalSearchResults(searchTerm, parsedDoc).then((localMarkup) => ({
          markup: localMarkup || resultsMarkup,
          // Only cache when we actually have useful results, or true empty after catalog is ready
          cacheable: Boolean(localMarkup) || PredictiveSearch.isCatalogReady(),
        }));
      })
      .then((result) => {
        if (!result?.markup) return;
        if (requestId !== this.requestId || this.getQuery() !== searchTerm) return;

        if (result.cacheable) {
          this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
            predictiveSearchInstance.cachedResults[queryKey] = result.markup;
          });
        }

        this.renderSearchResults(result.markup);
        searchDeferred?.resolve({ totalCount: this.getTotalResultCount() });
      })
      .catch((error) => {
        if (error?.name === 'AbortError' || error?.code === 20) {
          searchDeferred?.reject(error);
          return;
        }
        searchDeferred?.reject(error);
        this.close();
        throw error;
      });
  }

  buildLocalSearchResults(searchTerm, shopifyDoc) {
    return PredictiveSearch.ensureCatalog()
      .then(() => {
        const products = PredictiveSearch.matchProducts(searchTerm);
        const collections = PredictiveSearch.matchCollections(searchTerm);
        const queries = PredictiveSearch.matchQuerySuggestions(searchTerm, products, collections);
        const shopifySuggestionsHtml = shopifyDoc?.querySelector('#predictive-search-results-queries-list')?.innerHTML || '';

        if (
          !products.length &&
          !collections.length &&
          !queries.length &&
          !shopifySuggestionsHtml.trim()
        ) {
          return null;
        }

        return PredictiveSearch.buildLocalResultsMarkup({
          products,
          collections,
          queries,
          searchTerm,
          instance: this,
          shopifySuggestionsHtml,
        });
      })
      .catch(() => null);
  }

  static isCatalogReady() {
    return Array.isArray(this.productIndex) && this.productIndex.length > 0;
  }

  static ensureCatalog() {
    if (this.isCatalogReady()) {
      return Promise.resolve({
        products: this.productIndex,
        collections: this.collectionIndex,
      });
    }

    if (!this.catalogPromise) {
      this.catalogPromise = Promise.all([this.fetchAllProducts(), this.fetchAllCollections()])
        .then(([products, collections]) => {
          this.productIndex = products;
          this.collectionIndex = collections;
          return { products, collections };
        })
        .catch(() => {
          this.catalogPromise = null;
          return { products: [], collections: [] };
        });
    }

    return this.catalogPromise;
  }

  static fetchAllProducts() {
    const loadPage = (page, products = []) => {
      return fetch(`/collections/all/products.json?limit=250&page=${page}`)
        .then((response) => {
          if (!response.ok) throw new Error(response.status);
          return response.json();
        })
        .then((data) => {
          const pageProducts = data.products || [];
          const combinedProducts = products.concat(pageProducts);

          if (pageProducts.length < 250) return combinedProducts;
          return loadPage(page + 1, combinedProducts);
        });
    };

    return loadPage(1).catch(() => []);
  }

  static fetchAllCollections() {
    return fetch('/collections.json?limit=250')
      .then((response) => {
        if (!response.ok) throw new Error(response.status);
        return response.json();
      })
      .then((data) =>
        (data.collections || []).filter((collection) => {
          // Hide draft / unpublished collections from suggestions
          if (!collection) return false;
          if (collection.published_at === null || collection.published_at === '') return false;
          return true;
        })
      )
      .catch(() => []);
  }

  static matchProducts(searchTerm) {
    const query = searchTerm.trim().toLowerCase();
    if (!query.length) return [];

    return this.productIndex
      .map((product) => ({
        product,
        score: this.getTextMatchScore(
          [
            product.title,
            product.handle,
            product.vendor,
            product.product_type,
            ...(product.tags || []),
          ],
          query
        ),
      }))
      .filter(({ score }) => score > 0)
      .sort((a, b) => b.score - a.score || a.product.title.localeCompare(b.product.title))
      .slice(0, 4)
      .map(({ product }) => product);
  }

  static matchCollections(searchTerm) {
    const query = searchTerm.trim().toLowerCase();
    if (!query.length) return [];

    return this.collectionIndex
      .map((collection) => ({
        collection,
        score: this.getTextMatchScore([collection.title, collection.handle], query),
      }))
      .filter(({ score }) => score > 0)
      .sort((a, b) => b.score - a.score || a.collection.title.localeCompare(b.collection.title))
      .slice(0, 4)
      .map(({ collection }) => collection);
  }

  static matchQuerySuggestions(searchTerm, products = [], collections = []) {
    const query = searchTerm.trim().toLowerCase();
    if (!query.length) return [];

    const suggestions = new Map();

    const addSuggestion = (text, score = 50) => {
      const clean = String(text || '').trim();
      if (!clean) return;
      const key = clean.toLowerCase();
      if (key === query) return;
      if (!key.startsWith(query) && !key.split(/[\s\-_/]+/).some((word) => word.startsWith(query))) return;
      if (!suggestions.has(key) || suggestions.get(key).score < score) {
        suggestions.set(key, { text: clean, score });
      }
    };

    products.forEach((product) => {
      addSuggestion(product.title, 90);
      String(product.title || '')
        .split(/[\s\-_/]+/)
        .forEach((word) => addSuggestion(word, 80));
    });

    collections.forEach((collection) => {
      addSuggestion(collection.title, 85);
    });

    this.productIndex.forEach((product) => {
      String(product.title || '')
        .split(/[\s\-_/]+/)
        .forEach((word) => {
          if (word.toLowerCase().startsWith(query)) addSuggestion(word, 70);
        });
    });

    return Array.from(suggestions.values())
      .sort((a, b) => b.score - a.score || a.text.localeCompare(b.text))
      .slice(0, 4);
  }

  static getTextMatchScore(values, query) {
    let bestScore = 0;

    values.forEach((value) => {
      const text = String(value || '').toLowerCase();
      if (!text) return;

      const words = text.split(/[\s\-_/]+/).filter(Boolean);

      if (words.some((word) => word.startsWith(query))) bestScore = Math.max(bestScore, 100);
      else if (text.startsWith(query)) bestScore = Math.max(bestScore, 90);
      else if (text.includes(query)) bestScore = Math.max(bestScore, 80);
      else if (words.some((word) => word.includes(query))) bestScore = Math.max(bestScore, 70);
    });

    return bestScore;
  }

  static buildLocalResultsMarkup({
    products,
    collections,
    queries = [],
    searchTerm,
    instance,
    shopifySuggestionsHtml = '',
  }) {
    const productsHeading = instance.getAttribute('data-products-heading') || 'Products';
    const suggestionsHeading = instance.getAttribute('data-suggestions-heading') || 'Suggestions';
    const searchForTemplate = instance.getAttribute('data-search-for-template') || 'Search for “TERM”';
    const searchForText = searchForTemplate.replace('TERM', searchTerm);

    const localQueryItems = queries
      .map((querySuggestion, index) => this.buildLocalQueryItemMarkup(querySuggestion, index + 1, searchTerm))
      .join('');
    const localCollectionItems = collections
      .map((collection, index) => this.buildLocalCollectionItemMarkup(collection, index + 1))
      .join('');

    const hasShopifySuggestions = Boolean(shopifySuggestionsHtml.trim());
    const hasLocalSuggestions = Boolean(localQueryItems || localCollectionItems);
    const hasSuggestions = hasShopifySuggestions || hasLocalSuggestions;
    const hasProducts = products.length > 0;

    const suggestionItems = `${hasShopifySuggestions ? shopifySuggestionsHtml : ''}${localQueryItems}${localCollectionItems}`;
    const productItems = products.map((product, index) => this.buildLocalProductItemMarkup(product, index + 1)).join('');

    const suggestionsBlock = hasSuggestions
      ? `
        <div>
          <h2 id="predictive-search-queries" class="predictive-search__heading text-body caption-with-letter-spacing">
            ${this.escapeHtml(suggestionsHeading)}
          </h2>
          <ul
            id="predictive-search-results-queries-list"
            class="predictive-search__results-list list-unstyled"
            role="group"
            aria-labelledby="predictive-search-queries"
          >
            ${suggestionItems}
          </ul>
        </div>
      `
      : '';

    const productsBlock = hasProducts
      ? `
        <div>
          <h2 id="predictive-search-products" class="predictive-search__heading text-body caption-with-letter-spacing">
            ${this.escapeHtml(productsHeading)}
          </h2>
          <ul
            id="predictive-search-results-products-list"
            class="predictive-search__results-list list-unstyled"
            role="group"
            aria-labelledby="predictive-search-products"
          >
            ${productItems}
          </ul>
        </div>
      `
      : '';

    const firstColumn = hasSuggestions
      ? `
        <div class="predictive-search__result-group">
          ${suggestionsBlock}
        </div>
      `
      : '';

    const secondColumn = hasProducts
      ? `
        <div class="predictive-search__result-group">
          ${productsBlock}
        </div>
      `
      : '';

    const wrapperClass = [
      'predictive-search__results-groups-wrapper',
      !hasProducts ? 'predictive-search__results-groups-wrapper--no-products' : '',
      !hasSuggestions ? 'predictive-search__results-groups-wrapper--no-suggestions' : '',
    ]
      .filter(Boolean)
      .join(' ');

    const totalResults = products.length + collections.length + queries.length;

    return `
      <div id="predictive-search-results" role="listbox">
        <div id="predictive-search-results-groups-wrapper" class="${wrapperClass}">
          ${firstColumn}
          ${secondColumn}
        </div>
        <div id="predictive-search-option-search-keywords" class="predictive-search__search-for-button">
          <button
            class="predictive-search__item predictive-search__item--term link link--text h5 animate-arrow"
            tabindex="-1"
            role="option"
            aria-selected="false"
          >
            <span data-predictive-search-search-for-text>${this.escapeHtml(searchForText)}</span>
          </button>
        </div>
        <span
          class="hidden"
          data-predictive-search-live-region-count-value
          data-total-results="${totalResults}"
        >
          ${totalResults} result${totalResults === 1 ? '' : 's'} found for "${this.escapeHtml(searchTerm)}"
        </span>
      </div>
    `;
  }

  static buildLocalQueryItemMarkup(querySuggestion, index, searchTerm) {
    const text = querySuggestion.text || '';
    const lowerText = text.toLowerCase();
    const lowerTerm = searchTerm.toLowerCase();
    let styledText = this.escapeHtml(text);

    if (lowerText.startsWith(lowerTerm) && lowerTerm.length) {
      styledText = `<mark>${this.escapeHtml(text.slice(0, searchTerm.length))}</mark>${this.escapeHtml(
        text.slice(searchTerm.length)
      )}`;
    }

    const searchUrl = `/search?q=${encodeURIComponent(text)}&type=product&options%5Bprefix%5D=last`;

    return `
      <li
        id="predictive-search-option-query-local-${index}"
        class="predictive-search__list-item"
        role="option"
        aria-selected="false"
      >
        <a href="${searchUrl}" class="predictive-search__item link link--text" tabindex="-1">
          <div class="predictive-search__item-content predictive-search__item-content--centered">
            <p
              class="predictive-search__item-heading predictive-search__item-query-result h5"
              aria-label="${this.escapeHtml(text)}"
            >
              ${styledText}
            </p>
          </div>
        </a>
      </li>
    `;
  }

  static buildLocalCollectionItemMarkup(collection, index) {
    return `
      <li
        id="predictive-search-option-collection-local-${index}"
        class="predictive-search__list-item"
        role="option"
        aria-selected="false"
      >
        <a href="${this.escapeHtml(collection.url || `/collections/${collection.handle}`)}" class="predictive-search__item link link--text" tabindex="-1">
          <div class="predictive-search__item-content predictive-search__item-content--centered">
            <p class="predictive-search__item-heading h5">${this.escapeHtml(collection.title || '')}</p>
          </div>
        </a>
      </li>
    `;
  }

  static buildLocalProductItemMarkup(product, index) {
    const variant = product.variants?.[0] || {};
    const image = product.images?.[0];
    const imageSrc = image?.src ? `${image.src}${image.src.includes('?') ? '&' : '?'}width=240` : '';
    const imageMarkup = imageSrc
      ? `<img
          class="predictive-search__image"
          src="${this.escapeHtml(imageSrc)}"
          alt="${this.escapeHtml(product.title || '')}"
          width="64"
          height="64"
        >`
      : '';
    const payload = this.escapeHtml(
      JSON.stringify({
        product: {
          id: product.id,
          title: product.title,
          handle: product.handle,
          selectedVariant: null,
        },
        context: 'search',
        selectedOptions: [],
      })
    );

    return `
      <li
        id="predictive-search-option-product-${index}"
        class="predictive-search__list-item"
        role="option"
        aria-selected="false"
      >
        <product-component view-event-payload="${payload}">
          <a
            href="/products/${this.escapeHtml(product.handle)}"
            class="predictive-search__item predictive-search__item--link-with-thumbnail link link--text"
            tabindex="-1"
          >
            ${imageMarkup}
            <div class="predictive-search__item-content">
              <p class="predictive-search__item-heading h5">${this.escapeHtml(product.title || '')}</p>
              ${this.buildLocalPriceMarkup(variant)}
            </div>
          </a>
        </product-component>
      </li>
    `;
  }

  static buildLocalPriceMarkup(variant) {
    const price = parseFloat(variant.price);
    const compareAtPrice = parseFloat(variant.compare_at_price);
    const formattedPrice = this.formatProductPrice(price);
    const onSale = compareAtPrice > price;

    if (!onSale) {
      return `
        <div class="price">
          <div class="price__container">
            <div class="price__regular">
              <span class="price-item price-item--regular">${formattedPrice}</span>
            </div>
          </div>
        </div>
      `;
    }

    const discountPct = Math.round(((compareAtPrice - price) * 100) / compareAtPrice);
    const discountMarkup =
      discountPct > 0 ? `<span class="price-discount">-${discountPct}%</span>` : '';

    return `
      <div class="price price--on-sale">
        <div class="price__container">
          <div class="price__regular">
            <span class="price-item price-item--regular">${formattedPrice}</span>
          </div>
          <div class="price__sale">
            <span>
              <s class="price-item price-item--regular">${this.formatProductPrice(compareAtPrice)}</s>
            </span>
            <span class="price-item price-item--sale price-item--last">${formattedPrice}</span>
            ${discountMarkup}
          </div>
        </div>
      </div>
    `;
  }

  static formatProductPrice(amount) {
    const value = Math.round(parseFloat(amount) || 0);
    return `Rs.${value.toLocaleString('en-PK')} PKR`;
  }

  static escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  getTotalResultCount() {
    return parseInt(this.predictiveSearchResults.querySelector('[data-total-results]')?.dataset.totalResults) || 0;
  }

  dispatchSearchUpdateEvent(query) {
    const { SearchUpdateEvent } = window.StandardEvents || {};
    if (!SearchUpdateEvent) return null;

    const deferred = SearchUpdateEvent.createPromise();
    this.dispatchEvent(
      new SearchUpdateEvent({
        search: { query },
        promise: deferred.promise,
      })
    );
    return deferred;
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) return;
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]')?.textContent || '');
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
